/**
 * HubAssignmentCard — 학습(/students-hub) 활동 피드의 "시험지 배정 카드"(좌측 말풍선)
 *
 * ══ 왜 카드가 따로 있나 ═════════════════════════════════════════════════
 * 피드의 테스트 카드(HubTestHistoryItem)가 그리는 것은 v1 `/api/exams/summary` 뿐이다.
 * 신규 시험지(v2)는 그 응답에 **설계상 들어오지 않는다** — 스탬프를 쓰지 않기 때문이다
 * (`docs/exam-builder-v2/BE-ref-05-v2-design-decisions.md` E1·E2 · §1-5 「/summary 는 손대지 않음」).
 * 백엔드 감사도 같은 말을 남겼다: 「학생 앱·학생허브 테스트 이력에 신규 시험지가 전혀 안 보임 …
 * 사실이나 결함 아님 — 다음 단계 계획 항목」(`BE-20260826-full-audit.md:333`).
 *
 * 그 「다음 단계」가 이 카드다. v2 배정은 `GET /students/{sid}/assignments` 로 따로 받아
 * (examPaperApi.fetchStudentAssignments) 같은 피드에 시계열로 섞는다. 이것이 없으면
 * **v2 시험지를 출제해도 학생 화면에는 아무 일도 일어나지 않는다.**
 * ══════════════════════════════════════════════════════════════════════
 *
 * 행 = 배정 상세와 동형 + `paper_id`(contract-answers §1-1·§2-9):
 *   {assignment_id, paper_id, paper_revision, exam_type, exam_round, exam_date,
 *    open_at, close_at, status, is_closed, difficulty, total_items, graded, assigned_at, closed_at}
 * `paper_title` 은 배정 **상세**에 있다고만 계약에 적혀 있어(fe-handover §핵심), 목록 행에서는
 * 없을 수 있다 — 없으면 「시험지 #{paper_id}」로 대신한다. 지어내지 않는다.
 *
 * ══ 카드 본체와 [>] 가 하는 일이 다르다 ═══════════════════════════════
 * 2026-09-07 오너 지시로 갈렸다. 종전에는 **카드 어디를 눌러도 채점 창**이 떴다.
 *   · 카드 본체 → **고르기.** 구 테스트 카드(HubTestHistoryItem)와 같은 얼굴로 골라지고
 *     (`border-transparent ring-2 ring-primary`), 문제 미리보기가 그 시험지로 좁혀진다.
 *   · 오른쪽 [>] → **채점 창.** 창을 여는 길은 이 버튼 하나다.
 *
 * 그래서 뿌리가 `button` 에서 `div[role=button]` 으로 내려왔다 — 버튼 안에 버튼을 넣을 수 없다.
 * 구 테스트 카드가 이미 같은 모양이다(카드는 div, 안의 상태 아이콘만 버튼). 키보드로도 고를 수
 * 있게 `tabIndex`·Enter·Space 를 손으로 붙인다(구 카드에는 없는 것이라 여기서 후퇴시키지 않는다).
 *
 * [>] 는 `onOpen` 이 없으면 채점 화면 주소로 가는 진짜 링크가 된다 — 새 탭 열기 같은 브라우저
 * 기본 동작이 그 자리에 남고, 학생 화면으로 돌아올 주소는 route state 로 잇는다.
 * ══════════════════════════════════════════════════════════════════════
 */

import { forwardRef } from 'react';
import { CalendarClock, CircleCheck, ClipboardList, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { ASSIGN_STATUS, assignStatusDisplay, fmtServerDateTime } from '../ExamPapersV2/examPaperApi';

/** 시험구분·차수는 둘 다 선택값이라 있는 것만 이어 붙인다(AssignPanel.examLabel 과 같은 규칙) */
const examLabel = (a) => {
  const parts = [];
  if (a?.exam_type) parts.push(String(a.exam_type));
  if (a?.exam_round != null && a.exam_round !== '') parts.push(`${a.exam_round}차`);
  return parts.join(' ');
};

/**
 * 상태별 아이콘. 같은 클립보드가 **색으로만** 채점 여부를 말한다(2026-09-06 오너 지시):
 *   채점 완료(200·is_closed) = 메인 초록(primary) / 미채점(100) = 회색 400 / 미상 = 회색 300.
 * 100 을 초록으로 두면 채점이 끝난 것처럼 보였고, 완료를 회색 체크(ClipboardCheck)로 두면
 * 끝난 것이 꺼진 것처럼 보였다. 예약(50)만 시계 아이콘·보라로 따로 선다.
 * 상태를 모르면(옛 응답·미상) 가장 옅은 회색을 두어 어느 상태로도 오인시키지 않는다.
 */
const statusIcon = (a) => {
  if (a?.status === 200 || a?.is_closed) return { Icon: ClipboardList, cls: 'text-primary' };
  if (a?.status === 50) return { Icon: CalendarClock, cls: 'text-violet-500' };
  if (a?.status === 100) return { Icon: ClipboardList, cls: 'text-gray-400' };
  return { Icon: ClipboardList, cls: 'text-gray-300' };
};

/**
 * 배지 문구와 색. 공용 ASSIGN_STATUS 는 100 을 초록 「진행 중」이라 부르지만 **이 카드에서만
 * 주황 「미채점」**으로 쓴다(2026-09-06 오너 지시).
 *
 * 배정 목록(AssignPanel)·채점 화면(AssignmentPanel)은 배포 창의 열림·닫힘을 말하는 자리라
 * 「진행 중」이 맞다 — 시험이 잘 돌아가고 있다는 뜻이니 초록이다. 그러나 학생 피드에서 보는
 * 사람이 묻는 것은 「채점했나」이고, 그 답이 「아직」이면 **할 일이 남았다**는 뜻이다.
 * 초록은 다 됐다는 신호라 그 자리에서 거짓말을 한다. 그래서 글자와 함께 색도 덮는다 —
 * 주황(amber)은 이 저장소가 「아직·주의」에 쓰는 계열이다(예약생 배지·미저장 경고 띠와 같다).
 * 공용 상수는 그대로 두고 이 카드에서만 덮는다. 다른 상태(예약·채점 완료)는 공용 그대로.
 */
const statusBadge = (a) => {
  const st = assignStatusDisplay(a);
  return st === ASSIGN_STATUS[100]
    ? { ...st, text: '미채점', cls: 'bg-amber-50 text-amber-700' }
    : st;
};

const HubAssignmentCard = forwardRef(function HubAssignmentCard({
  assignment,
  compact = false,
  recent = false,
  returnTo = null,
  /**
   * [>]를 누르면 채점 창을 **이 화면 위에** 연다(오너 지시 2026-09-06) — 페이지를 옮기지 않는다.
   * 주지 않으면 [>]가 채점 화면 주소로 가는 링크가 된다(딥링크·다른 화면에서 쓰는 길).
   */
  onOpen = null,
  /** 카드 본체를 누르면 부른다 — 이 배정을 고른다(문제 미리보기를 이 시험지로 좁힌다) */
  onSelect = null,
  /** 지금 골라져 있는가 — 구 테스트 카드와 같은 얼굴로 칠한다 */
  selected = false,
}, ref) {
  const aid = assignment?.assignment_id;
  const status = statusBadge(assignment);
  const closed = assignment?.status === 200 || assignment?.is_closed;
  const { Icon, cls } = statusIcon(assignment);
  const title = assignment?.paper_title || (assignment?.paper_id != null ? `시험지 #${assignment.paper_id}` : '시험지');
  // 부제 = 시험구분 · 배정 시각. **시각을 제목 줄에 두지 않는다** — 「YYYY-MM-DD HH:mm」이 16자라
  // 좁은 폭(패널 360px)에서 제목을 반 넘게 잘라먹었다(실측: 「9월 대비 시험지 (홍…」에서 끊김).
  // 리포트 카드가 `#33 · 2026.09.02 19:00` 을 부제 줄에 두는 것과 같은 이유다.
  const sub = [examLabel(assignment), fmtServerDateTime(assignment?.assigned_at)].filter(Boolean).join(' · ');
  const total = Number(assignment?.total_items);
  const graded = Number(assignment?.graded);
  const hasCount = Number.isFinite(total) && total > 0;
  // 채점 진행은 「몇 문항 중 몇 개」다. 정답률이 아니다 — 배정 목록에는 정오답 집계가 오지 않는다.
  const gradedText = hasCount ? `${Number.isFinite(graded) ? graded : 0}/${total}` : '—';
  // 난이도는 100배 정수로 온다 — 사람이 읽는 자리에서는 1~5 척도로 되돌린다(316 → 3.16)
  const rawDifficulty = Number(assignment?.difficulty);
  const difficultyText = Number.isFinite(rawDifficulty) && rawDifficulty > 0
    ? (rawDifficulty / 100).toFixed(2)
    : '-';

  const c = {
    pad: compact ? 'p-2.5' : 'p-[14.4px]',
    round: compact ? 'rounded-2xl' : 'rounded-3xl',
    title: compact ? 'text-[12px]' : 'text-[14px]',
    sub: compact ? 'text-[10px]' : 'text-xs',
    icon: compact ? 20 : 28,
    /**
     * [>] 의 자리 — 구 테스트 카드의 상태 아이콘 버튼과 **같은 값**이다
     * (HubTestHistoryItem c.iconBtn·c.iconSize). 두 카드가 나란히 서는 피드에서, 카드 안에서
     * 다른 화면을 여는 버튼이라는 성격도 같다. 종전 14px 짜리 장식 같던 꺾쇠를 오너 지시로
     * 키우면서(2026-09-07) 크기를 새로 발명하지 않고 그 형제 값을 가져왔다.
     */
    openBtn: compact ? 'w-6 h-6 min-w-[24px] min-h-[24px]' : 'w-[32px] h-[32px] min-w-[32px] min-h-[32px]',
    openIcon: compact ? 20 : 32,
  };

  // 고를 수 있는 카드인가 — 번호가 없으면 가리킬 배정도 없다
  const selectable = onSelect != null && aid != null;
  const handleSelect = () => { if (selectable) onSelect(assignment); };

  return (
    <div
      ref={ref}
      /* 카드를 집는 손잡이. 종전에는 `a[href=/exam-papers/assignment/{aid}]` 가 그 일을 했는데,
         카드가 링크이길 그만두면서 잡을 곳이 클래스밖에 남지 않았다 — 그 자리를 이름으로 둔다. */
      data-assignment-id={aid ?? undefined}
      role={selectable ? 'button' : undefined}
      /* recent(방금 출제) 카드는 상위가 focus() 로 데려간다 — 고를 수 없는 카드여도 초점은 받아야 한다 */
      tabIndex={selectable ? 0 : -1}
      onClick={selectable ? handleSelect : undefined}
      onKeyDown={selectable ? (e) => {
        // 버튼이 아니라 div 라 스페이스·엔터를 손으로 받는다. 스페이스는 기본이 쪽 넘김이라 막는다.
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleSelect(); }
      } : undefined}
      aria-pressed={selectable ? selected : undefined}
      title={selectable ? '이 시험지의 문제를 미리보기에 띄웁니다' : undefined}
      /* 선택 얼굴은 구 테스트 카드와 같은 값이다(HubTestHistoryItem) — 같은 피드에 나란히 서는
         카드라 골라진 표시가 다르면 무엇이 골라졌는지 눈이 두 번 헤맨다. */
      className={`group block w-full border bg-white text-left shadow-sm transition-all duration-300 hover:scale-[1.02] hover:shadow-2xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 ${c.round} ${c.pad} ${
        selectable ? 'cursor-pointer' : ''
      } ${selected ? 'border-transparent ring-2 ring-primary' : 'border-gray-200 hover:border-gray-300'}`}
    >
      <div className="mb-1 flex items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2">
          <Icon size={c.icon} className={`shrink-0 ${cls}`} />
          <div className="min-w-0">
            <div className="flex items-center gap-1.5">
              <h3 className={`${c.title} truncate font-semibold`}>{title}</h3>
              {recent && (
                <span className="shrink-0 rounded-full bg-primary/10 px-1.5 py-0.5 text-[9px] font-bold text-primary">
                  방금 출제
                </span>
              )}
              {closed ? (
                <span
                  aria-label="채점 완료"
                  title="채점 완료"
                  className="inline-flex shrink-0 items-center justify-center text-primary"
                >
                  <CircleCheck aria-hidden="true" className="size-4" strokeWidth={2.5} />
                </span>
              ) : (
                <span className={`shrink-0 rounded-full px-1.5 py-0.5 text-[9px] font-bold ${status.cls}`}>
                  {status.text}
                </span>
              )}
            </div>
            <p className={`${c.sub} truncate text-gray-500`}>{sub || '시험지 출제'}</p>
            {/* 시각을 못 읽으면 부제가 비어 「시험지 출제」만 남는다 — 그 자리를 지어내지 않는다 */}
          </div>
        </div>
        {/* 채점 창으로 가는 문. 카드 본체는 「고르기」라, 창을 여는 길은 이 버튼 하나뿐이다.
            stopPropagation 이 빠지면 창을 열면서 카드도 함께 골라진다 — 한 번 누른 것이 두 일을
            하면 어느 쪽을 되돌려야 하는지 알 수 없다(구 테스트 카드의 상태 아이콘과 같은 자). */}
        {aid != null && (onOpen ? (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onOpen(aid); }}
            title="채점 창을 엽니다"
            aria-label="채점 창 열기"
            className={`${c.openBtn} flex shrink-0 items-center justify-center rounded-full text-gray-300 transition-colors hover:bg-gray-100 hover:text-primary focus:outline-none focus:ring-2 focus:ring-primary/20 group-hover:text-primary`}
          >
            <ChevronRight size={c.openIcon} />
          </button>
        ) : (
          <Link
            to={`/exam-papers/assignment/${aid}`}
            state={{ returnTo }}
            onClick={(e) => e.stopPropagation()}
            title="채점 화면으로 이동합니다"
            aria-label="채점 화면으로 이동"
            className={`${c.openBtn} flex shrink-0 items-center justify-center rounded-full text-gray-300 transition-colors hover:bg-gray-100 hover:text-primary focus:outline-none focus:ring-2 focus:ring-primary/20 group-hover:text-primary`}
          >
            <ChevronRight size={c.openIcon} />
          </Link>
        ))}
      </div>

      {/* 한 줄 통계 — 테스트 카드의 collapsed 줄과 같은 결(TID·난이도·문항수·채점) */}
      <div className="flex items-center justify-between whitespace-nowrap text-[10px] text-gray-500">
        <span>
          {/* 배정 번호를 부르는 이름은 TID 다(오너 2026-09-06) — 레거시 결과표가 시험 한 건을
              그렇게 부르고, v2 에서 그 한 건에 해당하는 것이 배정이다 */}
          <span className="text-gray-400">TID </span>
          <span className="font-semibold text-gray-800">{aid ?? '-'}</span>
        </span>
        <span>
          <span className="text-gray-400">난이도 </span>
          {/* 서버는 난이도를 100배 정수로 준다(316 = 3.16). 이 저장소가 네 곳에서 같은 자로
              되돌린다 — ReportModal:1906 · PresetTable:52 · Datapage:728 · useTests:135 */}
          <span className="font-semibold text-gray-800">{difficultyText}</span>
        </span>
        <span>
          <span className="text-gray-400">문항 </span>
          <span className="font-semibold text-gray-800">{hasCount ? total : '-'}</span>
        </span>
        <span>
          <span className="text-gray-400">{closed ? '채점 완료 ' : '채점 '}</span>
          <span className="font-semibold text-primary">{gradedText}</span>
        </span>
      </div>
    </div>
  );
});

export default HubAssignmentCard;
